import type { ReactNode } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useProfileQuery } from "@/modules/profile/services/useProfileQuery";
import { ProfileForm } from "@/modules/profile/components/ProfileForm";
import { ProfilePasswordForm } from "@/modules/profile/components/ProfilePasswordForm";
import { Card, ErrorState, LoadingState, useToast } from "@/shared/rd";

/**
 * The signed-in admin's own account, on the Settings recipe: one card per
 * section with row fields. Edits raise a save bar under the section, and a
 * save shows a toast.
 */
export function ProfilePage() {
  const t = useTranslations();
  const { data: admin, isLoading, error, refetch, isRefetching } = useProfileQuery();
  const toast = useToast();
  const onSaved = (message: string) => void toast.show({ message });

  if (isLoading) {
    return <LoadingState title={t(keys.shell.loadingTitle, { page: t("admin.profilePage") })} sub={t(keys.shell.loadingSub)} rows={0} />;
  }

  if (error || !admin) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: t("admin.profilePage") })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={isRefetching ? t(keys.shell.retrying) : t(keys.shell.retry)}
        retrying={isRefetching}
      />
    );
  }

  return (
    <>
      <SettingsCard title={t(keys.admin.profileTitle)} note={t("admin.profileNote")}>
        <ProfileForm
          defaultValues={{ name: admin.name, email: admin.email, phone: admin.phone }}
          onSaved={onSaved}
        />
      </SettingsCard>
      <SettingsCard title={t(keys.admin.passwordTitle)} note={t("admin.passwordNote")}>
        <ProfilePasswordForm onSaved={onSaved} />
      </SettingsCard>
    </>
  );
}

/** A settings section: a 15/600 title, a one-line note, then its fields. */
function SettingsCard({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <Card>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h2>
        <span style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>{note}</span>
      </div>
      {children}
    </Card>
  );
}
